// src/pages/WishlistPage.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { LanguageContext } from "../context/LanguageContext";
import { usePriceVisibility } from "../context/PriceVisibilityContext";
import { getMyWishlist } from "../services/wishlist.api";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";

function WishlistProductCard({
  product,
  lang,
  onRemove,
  removing,
  canViewPrices,
}) {
  const navigate = useNavigate();

  const image =
    product.images?.[0]?.url ||
    product.images?.[0] ||
    product.image ||
    "/images/placeholder-product.jpg";

  const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(String(image).split("?")[0]);

  const name =
    lang === "AR"
      ? product.nameAR || product.name_ar || product.nameEN || product.name_en
      : product.nameEN || product.name_en || product.nameAR || product.name_ar;

  const price = Number(product.price || product.base_price || 0);

const discount = Number(
  product.discount ??
    product.sale_percent ??
    product.salePercent ??
    0
);

const hasDiscount = discount > 0;

const finalPrice = hasDiscount
  ? Math.round(price * (1 - discount / 100))
  : price;

  const slug = product.slug || product.seo_slug || "";

  const productUrl = slug
    ? `/product/${encodeURIComponent(slug)}`
    : null;

  const sizes = Array.isArray(product.sizes)
    ? product.sizes
        .map((s) =>
          typeof s === "string" || typeof s === "number"
            ? s
            : s?.size || s?.label || s?.name || s?.value || ""
        )
        .filter(Boolean)
    : [];

  const openProduct = () => {
    if (!productUrl) return;
    navigate(productUrl);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openProduct}
      className="
        w-full
        bg-white
        cursor-pointer
        text-center
      "
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* IMAGE */}
      <div className="relative w-full bg-gray-100 overflow-hidden">
        {isVideo ? (
          <video
            src={image}
            muted
            playsInline
            preload="metadata"
            className="w-full aspect-[3/4] object-cover block"
          />
        ) : (
          <img
            src={image}
            alt={name || ""}
            loading="lazy"
            className="w-full aspect-[3/4] object-cover block"
          />
        )}

{/* SALE BADGE */}
{canViewPrices && hasDiscount && (
  <div
    className="
      absolute top-3 right-0 z-20
      px-2 py-[4px]
      bg-red-600 text-white
      text-[11px] sm:text-[12px]
      font-bold tracking-wide
      uppercase
      leading-none
    "
  >
    -{discount}%
  </div>
)}

        {/* DELETE BADGE */}
       <button
  type="button"
  disabled={removing}
  onClick={(e) => {
    e.stopPropagation();
    if (!removing) onRemove(product);
  }}
  aria-label={lang === "AR" ? "إزالة من المفضلة" : "Remove from wishlist"}
  className="
    absolute top-0 left-0
    w-9 h-9
    bg-black/70
    text-white
    flex items-center justify-center
    z-20
    active:scale-95
    transition
    disabled:opacity-60
    disabled:cursor-wait
  "
>
         {removing ? (
  <span className="text-[18px] leading-none">…</span>
) : (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
)}
        </button>
      </div>

      {/* DETAILS */}
      <div className="pt-2 pb-4 px-1">
        <p
          className="
            mx-auto
            max-w-[92%]
            text-[13px] md:text-[14px]
            font-normal
            leading-[1.35]
            text-[#1f1f1f]
            line-clamp-3
            min-h-[48px]
          "
        >
          {name}
        </p>

       <div className="mt-1 flex items-center justify-center gap-2">
  {canViewPrices ? (
    <>
      <span
        className={`
          text-[17px] md:text-[18px]
          tracking-wide
          ${hasDiscount ? "font-bold text-red-600" : "font-normal text-[#1f1f1f]"}
        `}
      >
        ${Number(finalPrice || 0).toFixed(2)}
      </span>

      {canViewPrices && hasDiscount && (
        <span className="text-[13px] md:text-[14px] font-normal text-black line-through">
          ${Number(price || 0).toFixed(2)}
        </span>
      )}
    </>
  ) : (
    <span className="text-[12px] md:text-[13px] font-semibold uppercase tracking-wide text-black">
      {lang === "AR" ? "سجّل الدخول لمشاهدة السعر" : "Login to view price"}
    </span>
  )}
</div>

        {sizes.length > 0 && (
          <div className="mt-3 flex flex-wrap justify-start gap-1">
            {sizes.slice(0, 5).map((size, idx) => (
              <span
                key={`${size}-${idx}`}
                className="
                  inline-flex items-center justify-center
min-w-[26px]
h-[23px]
px-1
border border-black
bg-white
text-[11px]
font-normal
text-[#1f1f1f]
uppercase
leading-none
                "
              >
                {String(size).toUpperCase()}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function WishlistPage() {
  const { wishlist, toggleWishlist } = useCart();
  const { lang } = useContext(LanguageContext);
  const { pricesSalesVisible } = usePriceVisibility();
  const navigate = useNavigate();

  const [allProducts, setAllProducts] = useState([]);
const [wishlistLoading, setWishlistLoading] = useState(true);
const [wishlistSyncLoading, setWishlistSyncLoading] = useState(true);
const [removingId, setRemovingId] = useState(null);

const [canViewPrices, setCanViewPrices] = useState(() =>
  Boolean(localStorage.getItem("esill-token"))
);

useEffect(() => {
  const syncAuthState = () => {
    setCanViewPrices(Boolean(localStorage.getItem("esill-token")));
  };

  syncAuthState();

  window.addEventListener("auth-changed", syncAuthState);
  return () => window.removeEventListener("auth-changed", syncAuthState);
}, []);

  const TEXT = {
    EN: {
      breadcrumb: "Main page",
      empty: "Your favourite products will appear here.",
      seeNew: "See our new products!",
      loading: "Loading your wishlist...",
emptyTitle: "Your wishlist is empty",
    },
    AR: {
      breadcrumb: "الصفحة الرئيسية",
      empty: "ستظهر منتجاتك المفضلة هنا.",
      seeNew: "شاهدي المنتجات الجديدة!",
      loading: "جاري تحميل المفضلة...",
emptyTitle: "المفضلة فارغة",
    },
  }[lang];

  useEffect(() => {
  let mounted = true;
  const token = localStorage.getItem("esill-token");

  const loadWishlist = async () => {
    try {
      setWishlistSyncLoading(true);

      if (!token) return;

      const data = await getMyWishlist();

      if (!mounted) return;

      toggleWishlist({ __sync: true, items: data });
    } catch (err) {
      console.error("Failed to sync wishlist", err);
    } finally {
      if (mounted) {
        setWishlistSyncLoading(false);
      }
    }
  };

  loadWishlist();

  return () => {
    mounted = false;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  useEffect(() => {
  let mounted = true;

  setWishlistLoading(true);

  getAllProducts()
    .then((data) => {
      if (!mounted) return;
      setAllProducts(Array.isArray(data) ? data.map(mapBackendProduct) : []);
    })
    .catch(() => {
      if (mounted) setAllProducts([]);
    })
    .finally(() => {
      if (mounted) setWishlistLoading(false);
    });

  return () => {
    mounted = false;
  };
}, []);

const handleRemoveWishlist = async (product) => {
  if (!product?.id || removingId) return;

  try {
    setRemovingId(product.id);
    await toggleWishlist(product);
  } finally {
    setRemovingId(null);
  }
};

  const mergedWishlist = useMemo(() => {
    const fullById = new Map(allProducts.map((p) => [Number(p.id), p]));

    return wishlist.map((item) => {
      const full = fullById.get(Number(item.id));

      if (!full) return item;

      return {
  ...full,
  ...item,
  slug: full.slug || item.slug || item.seo_slug,
  images: full.images?.length ? full.images : item.images,
  sizes: full.sizes?.length ? full.sizes : item.sizes,

  // Keep fresh product pricing/sale info from full product data
  price: full.price ?? item.price,
  discount: full.discount ?? item.discount ?? item.sale_percent ?? 0,
};
    });
  }, [wishlist, allProducts]);

  const isEmpty = mergedWishlist.length === 0;

  const WishlistSkeleton = () => (
  <div className="w-full min-h-screen bg-white flex flex-col">
    <TopBanner />
    <MainHeader />

    <main
      dir={lang === "AR" ? "rtl" : "ltr"}
      className="
        flex-1
        w-full
        bg-white
        px-5 md:px-10
        pt-2 md:pt-44
        pb-16
      "
    >
      <div className="h-5 w-[160px] bg-gray-200 animate-pulse rounded mb-10 md:mb-14" />

      <div
        className="
          grid
          grid-cols-2
          md:grid-cols-3
          gap-x-5 gap-y-10
          md:gap-x-8 md:gap-y-14
          max-w-[1180px]
          mx-auto
        "
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="w-full">
            <div className="w-full aspect-[3/4] bg-gray-200 animate-pulse" />
            <div className="pt-3 pb-4 px-1 text-center">
              <div className="mx-auto h-4 w-[82%] bg-gray-200 animate-pulse rounded" />
              <div className="mx-auto mt-2 h-4 w-[64%] bg-gray-200 animate-pulse rounded" />
              <div className="mx-auto mt-3 h-5 w-[80px] bg-gray-200 animate-pulse rounded" />
            </div>
          </div>
        ))}
      </div>
    </main>

    <Footer className="mt-auto" />
  </div>
);

if (wishlistLoading || wishlistSyncLoading) {
  return <WishlistSkeleton />;
}

  return (
    <div className="w-full min-h-screen bg-white flex flex-col">
      <TopBanner />
      <MainHeader />

      <main
        dir={lang === "AR" ? "rtl" : "ltr"}
        className="
          flex-1
          w-full
          bg-white
          px-5 md:px-10
          pt-2 md:pt-44
          pb-16
        "
      >
        {/* BREADCRUMB */}
        <button
          type="button"
          onClick={() => navigate("/")}
          className="
            text-[15px] md:text-[17px]
            font-normal
            text-[#1f1f1f]
            flex items-center gap-2
            mb-10 md:mb-14
          "
        >
          <span>{TEXT.breadcrumb}</span>
          <span>{lang === "AR" ? "‹" : "›"}</span>
        </button>

        {/* EMPTY */}
        {isEmpty && (
  <div className="w-full max-w-[760px] mx-auto text-center pt-14 md:pt-20">
    <div
      className="
        mx-auto
        w-[66px] h-[66px]
        rounded-full
        border border-black
        flex items-center justify-center
        text-[34px]
        leading-none
        text-black
        mb-6
      "
    >
      ♡
    </div>

    <h1
      className="
        text-[26px] md:text-[38px]
        leading-[1.2]
        font-semibold
        text-black
        max-w-[760px]
        mx-auto
      "
    >
      {TEXT.emptyTitle}
    </h1>

    <p className="mt-4 text-[15px] md:text-[17px] text-gray-600 leading-7">
      {TEXT.empty}
    </p>

    <button
      type="button"
      onClick={() => navigate("/products?category=new-in")}
      className="
        mt-8
        w-full
        max-w-[420px]
        h-[58px] md:h-[64px]
        border-[2px] border-black
        bg-white
        text-black
        text-[20px] md:text-[24px]
        font-normal
        flex items-center justify-center gap-5
        hover:bg-black hover:text-white
        active:scale-[0.99]
        transition
        mx-auto
      "
    >
      <span>{TEXT.seeNew}</span>
      <span className="text-[30px] leading-none">♡</span>
    </button>
  </div>
)}

        {/* WISHLIST GRID */}
        {!isEmpty && (
          <div
            className="
              grid
              grid-cols-2
              md:grid-cols-3
              gap-x-5 gap-y-10
              md:gap-x-8 md:gap-y-14
              max-w-[1180px]
              mx-auto
            "
          >
            {mergedWishlist.map((item) => (
             <WishlistProductCard
  key={item.id}
  product={item}
  lang={lang}
  onRemove={handleRemoveWishlist}
  removing={Number(removingId) === Number(item.id)}
  canViewPrices={canViewPrices && pricesSalesVisible}
/>
            ))}
          </div>
        )}
      </main>

      <Footer className="mt-auto" />
    </div>
  );
}

export default WishlistPage;
